// ═══════════════════════════════════════════════════════════════════════════
// ADMIN: USERS
// Migration S4.2 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// `showUserModal` reste défini dans page-admin-groups (encore .js) et
// est exposé via window.showUserModal — déclaré dans core-globals.d.ts
// comme bare-ref cross-module.

import { buildUserRowHtml } from './page-users-row.js';
import { sortUsers } from './page-users-sort.js';

export interface UserEntity {
  id: number;
  trigram?: string | null;
  name: string;
  // État civil (août 2026) : colonnes Nom (= patronyme) et Prénom de la liste.
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  role?: string | null;
  is_team?: boolean;
  is_external?: boolean;
  is_active?: boolean;
  departure_date?: string | null;
  team_ids?: number[];
}

export type UserSortKey =
  | 'trigram'
  | 'type_trigram'
  | 'last_name'
  | 'first_name'
  | 'role'
  | 'ext'
  | 'teams'
  | 'email'
  | 'active';
export type SortDir = 'asc' | 'desc';

declare global {
  interface Window {
    renderUsersPage: typeof renderUsersPage;
  }
}

// `users.read` garde la route, mais c'est une clé de menu : elle n'autorise pas
// l'écriture (revue front §I3).
const canWriteUsers = (): boolean =>
  !!(window.hasPermission && window.hasPermission('users.write'));

pages.adminUsers = async function () {
  setHeader(
    t('users.page_title'),
    canWriteUsers()
      ? `<button class="btn btn-secondary ml-auto" id="btn-ei-users" style="margin-right:6px">${esc(t('common.export_import'))}</button>` +
          `<button class="btn btn-primary" id="btn-new-user">${esc(t('users.btn.add'))}</button>`
      : '',
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const [users, settings] = (await Promise.all([
      // Scope gestion : inclut les comptes admin techniques (DMO) pour qu'ils
      // restent éditables ici, tout en restant exclus des sélecteurs (règle 12).
      api.getUsers({ manage: true }),
      api.getTenantSettings().catch(() => ({ max_users: null }) as { max_users: number | null }),
    ])) as [UserEntity[], { max_users: number | null }];
    renderUsersPage(users, settings.max_users ?? null);
    document.getElementById('btn-new-user')?.addEventListener('click', () => {
      void showUserModal(null, users);
    });
    document.getElementById('btn-ei-users')?.addEventListener('click', () => {
      const examples = window.EXPORT_EXAMPLES['users'];
      window.showExportImportModal(
        'users',
        t('users.page_title'),
        examples?.json ?? '',
        examples?.csv ?? '',
      );
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function renderUsersPage(users: UserEntity[], maxUsers: number | null): void {
  let sortKey: UserSortKey = 'trigram';
  let sortDir: SortDir = 'asc';
  let fTeamId = '';
  // Filtre « afficher inactifs » (cf. user 2026-05-21) : décoché par défaut.
  let fShowInactive = false;

  const teams = users.filter((u) => u.is_team);
  const teamMap: Record<number, string> = Object.fromEntries(teams.map((u) => [u.id, u.name]));

  // Utilise le helper global isUserActive (cf. core.ts) — applique
  // is_active === false OR departure_date < today.
  const _isActive = (u: UserEntity): boolean => isUserActive(u as never);

  const getFilteredUsers = (): UserEntity[] => {
    let pool = users;
    if (!fShowInactive) pool = pool.filter((u) => u.is_team || _isActive(u));
    if (fTeamId) {
      const tid = parseInt(fTeamId);
      pool = pool.filter((u) => (u.is_team ? u.id === tid : (u.team_ids || []).includes(tid)));
    }
    return pool;
  };

  // Compteur : individus actifs / max (équipes exclues du décompte).
  const _activeCount = (): number => users.filter((u) => !u.is_team && _isActive(u)).length;

  const th = (key: UserSortKey, label: string): string => {
    const active = sortKey === key;
    const arrow = active ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
    return `<th class="sortable${active ? ' sorted' : ''}" data-sort-users="${key}" style="cursor:pointer;user-select:none">${esc(label)} <span class="sort-arrow" style="font-size:10px">${arrow}</span></th>`;
  };

  // Rendu d'une ligne : délégué au module frère (pur, `teamMap` immuable threadé).
  const buildUserRow = (u: UserEntity): string => buildUserRowHtml(u, teamMap, canWriteUsers());

  const renderTable = (): string => {
    const sorted = sortUsers(getFilteredUsers(), sortKey, sortDir);
    const rows = sorted.map(buildUserRow).join('');
    return `<div class="table-wrap"><table id="users-table">
      <thead><tr>
        ${th('trigram', t('users.col.trigram'))}
        ${th('last_name', t('users.col.name'))}
        ${th('first_name', t('users.col.first_name'))}
        ${th('type_trigram', t('users.col.type'))}
        ${th('ext', t('users.col.int_ext'))}
        ${th('active', t('users.col.active'))}
        ${th('role', t('users.col.role'))}
        ${th('teams', t('users.col.teams'))}
        ${th('email', t('users.col.email'))}
        <th></th>
      </tr></thead>
      <tbody id="users-tbody">${rows.length ? rows : `<tr><td colspan="10" class="empty">${esc(t('users.empty'))}</td></tr>`}</tbody>
    </table></div>`;
  };

  const teamFilterOpts =
    `<option value="">${esc(t('users.filter.team_all'))}</option>` +
    teams.map((tm) => `<option value="${tm.id}">${esc(tm.name)}</option>`).join('');

  // Compteur "actifs / max" affiché à droite de la barre de filtres.
  const counterHtml = (): string => {
    const n = _activeCount();
    const maxStr = maxUsers != null ? String(maxUsers) : '∞';
    const overLimit = maxUsers != null && n > maxUsers;
    return (
      `<span id="users-active-counter" style="margin-left:auto;font-size:12px;color:${overLimit ? 'var(--danger)' : 'var(--text2)'};font-weight:500">` +
      `${esc(t('users.counter.label'))} : <strong>${n}</strong> / ${esc(maxStr)}` +
      `</span>`
    );
  };

  setContent(`
    <div class="card" id="users-card">
      <div style="display:flex;align-items:center;gap:14px;margin-bottom:10px;flex-wrap:wrap">
        ${
          teams.length
            ? `<label style="font-size:12px;color:var(--text3);display:inline-flex;align-items:center;gap:6px">${esc(t('users.filter.team_label'))}
          <select id="users-team-filter" style="font-size:12px;padding:4px 8px;border:0.5px solid var(--border);border-radius:var(--radius)">${teamFilterOpts}</select>
        </label>`
            : ''
        }
        <label style="font-size:12px;color:var(--text3);display:inline-flex;align-items:center;gap:6px;cursor:pointer">
          <input type="checkbox" id="users-filter-inactive">
          ${esc(t('users.filter.show_inactive'))}
        </label>
        ${counterHtml()}
      </div>
      ${renderTable()}
    </div>`);

  const _refreshTbody = (): void => {
    const tbody = document.getElementById('users-tbody');
    if (!tbody) return;
    const sorted = sortUsers(getFilteredUsers(), sortKey, sortDir);
    tbody.innerHTML =
      sorted.map(buildUserRow).join('') ||
      `<tr><td colspan="10" class="empty">${esc(t('users.empty'))}</td></tr>`;
  };

  document.getElementById('users-team-filter')?.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLSelectElement | null;
    if (!target) return;
    fTeamId = target.value;
    _refreshTbody();
  });

  document.getElementById('users-filter-inactive')?.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    if (!target) return;
    fShowInactive = target.checked;
    _refreshTbody();
  });

  document.getElementById('users-card')?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const clickUser = target.closest<HTMLElement>('[data-click-user]');
    if (clickUser && !target.closest('button')) {
      const id = parseInt(clickUser.dataset['clickUser'] || '0');
      const u = users.find((x) => x.id === id);
      if (u) void showUserModal(u, users);
      return;
    }
    const sortTh = target.closest<HTMLElement>('[data-sort-users]');
    if (sortTh) {
      const key = sortTh.dataset['sortUsers'] as UserSortKey;
      if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      else {
        sortKey = key;
        sortDir = 'asc';
      }
      const tbody = document.getElementById('users-tbody');
      if (tbody) {
        const sorted = sortUsers(getFilteredUsers(), sortKey, sortDir);
        tbody.innerHTML =
          sorted.map(buildUserRow).join('') ||
          `<tr><td colspan="10" class="empty">${esc(t('users.empty'))}</td></tr>`;
      }
      document.querySelectorAll<HTMLElement>('[data-sort-users]').forEach((h) => {
        const a = h.dataset['sortUsers'] === sortKey;
        const arrow = h.querySelector('.sort-arrow');
        if (arrow) arrow.textContent = a ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
      });
      return;
    }
    const editBtn = target.closest<HTMLElement>('[data-edit-user]');
    if (editBtn) {
      const id = parseInt(editBtn.dataset['editUser'] || '0');
      const u = users.find((x) => x.id === id);
      if (u) void showUserModal(u, users);
      return;
    }
    const delBtn = target.closest<HTMLElement>('[data-del-user]');
    if (delBtn) {
      const id = parseInt(delBtn.dataset['delUser'] || '0');
      const name = delBtn.dataset['userName'] || '';
      openModal(
        t('users.modal.delete.title'),
        `<p>${t('users.modal.delete.message', { name })}</p>`,
        async () => {
          try {
            await api.deleteUser(id);
            closeModal();
            toast(t('users.toast.deleted'));
            _cache.users = null;
            void pages.adminUsers?.({});
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t('common.error');
            toast(msg, 'error');
          }
        },
        t('common.delete'),
      );
    }
  });
  document.getElementById('btn-new-user')?.addEventListener('click', () => {
    void showUserModal(null, users);
  });
}

// ── ESM globals shim (lot B.8 + S4.2) ──
window.renderUsersPage = renderUsersPage;

export {};
